import React, { useState } from 'react';
import { Box, Button, CircularProgress, Paper, Typography } from '@mui/material';
import { AnalyzeServiceProviderUnitPatternsResponse, UnitPatternModel } from '../../api-service/clients';
import { analyzeServiceProviderUnitPatterns } from './service-provider-unit-overview-api';
import { AiFeatureInfo } from '../citizen/serviceprovider/ai-demo/ai-feature-info';

interface UnitPatternsCardProps {
  serviceProviderUnitId: string;
  onHighlightCitizens: (citizenIds: string[]) => void;
}

export function UnitPatternsCard({ serviceProviderUnitId, onHighlightCitizens }: UnitPatternsCardProps) {
  const [analysis, setAnalysis] = useState<AnalyzeServiceProviderUnitPatternsResponse>();
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [selectedPattern, setSelectedPattern] = useState<UnitPatternModel>();

  async function analyze() {
    setIsAnalyzing(true);
    selectPattern(undefined);
    try {
      setAnalysis(await analyzeServiceProviderUnitPatterns(serviceProviderUnitId));
    } finally {
      setIsAnalyzing(false);
    }
  }

  function selectPattern(pattern: UnitPatternModel | undefined) {
    setSelectedPattern(pattern);
    onHighlightCitizens(pattern?.citizenIds ?? []);
  }

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box sx={{ flexGrow: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography variant="subtitle1">Mønstre på tværs af borgerne</Typography>
            <AiFeatureInfo feature="unitPatterns" />
          </Box>
          <Typography variant="caption">AI'en gennemgår zoneskift og magtanvendelser fra de seneste {analysis?.lookbackDays ?? 60} dage og peger på gentagelser.</Typography>
        </Box>
        <Button variant="contained" onClick={analyze} disabled={isAnalyzing} startIcon={isAnalyzing ? <CircularProgress size={16} /> : undefined}>
          {analysis ? 'Find mønstre igen' : 'Find mønstre'}
        </Button>
      </Box>
      {analysis && !isAnalyzing && (
        <Box sx={{ mt: 2 }}>
          {analysis.patterns.length === 0 ? (
            <Typography variant="body2">AI'en fandt ingen mønstre, der bygger på mindst to hændelser.</Typography>
          ) : (
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 2 }}>
              {analysis.patterns.map(pattern => {
                const isSelected = pattern === selectedPattern;
                return (
                  <Paper
                    key={pattern.title}
                    variant="outlined"
                    onClick={() => selectPattern(isSelected ? undefined : pattern)}
                    sx={{ p: 1.5, cursor: 'pointer', borderColor: isSelected ? 'primary.main' : undefined, bgcolor: isSelected ? 'action.selected' : undefined }}
                  >
                    <Typography variant="subtitle2">{pattern.title}</Typography>
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                      {pattern.description}
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
                      {pattern.suggestion}
                    </Typography>
                    <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                      {pattern.citizenIds.length === 1 ? '1 borger' : `${pattern.citizenIds.length} borgere`} · {pattern.eventCount} hændelser
                    </Typography>
                  </Paper>
                );
              })}
            </Box>
          )}
          <Typography variant="caption" display="block" sx={{ mt: 1.5 }}>
            AI-forslag – vurder selv. Klik på et mønster for at markere borgerne i tabellen. {analysis.analyzedCitizenIds.length} borgere indgik i analysen.
          </Typography>
        </Box>
      )}
    </Paper>
  );
}
